/**
 * @file LabelJobActions.tsx
 * @description Действия записи журнала печати этикеток: «Открыть» файл,
 *   «Перепечатать» и «⋯» с пунктом «Скрыть файл» (phase19.22, W17-9c; шаг 5 —
 *   скрытие вручную, решение пользователя 2026-09-26).
 *
 * Скрытый файл (устарел или скрыт вручную) — вместо «Открыть» пометка
 * «файл скрыт · дата», перепечатка остаётся: она берёт текущие данные.
 * Пункт «Скрыть файл» есть только у доступного файла — у скрытого «⋯» нет
 * вовсе (других пунктов в меню пока нет). Подтверждение — у журнала
 * (`LabelJobHideConfirm`), здесь только запрос на него.
 */

import { EyeOff, ExternalLink, Loader2, RotateCcw } from "lucide-react";

import { LABEL_TEXT, fileHiddenText } from "./label-texts";
import { Button } from "../../ui/button";
import { RowActionsMenu } from "../../ui/RowActionsMenu";
import { formatDocDateTime } from "../wms-document-meta";
import type { LabelPrintJobDto } from "../../../lib/api-labels";

/** Файл задачи: ссылка, «файл скрыт · дата» или «не сформирован». */
function FileCell({ job }: { job: LabelPrintJobDto }) {
  const file = job.file;
  if (file.state === "available") {
    return (
      <Button variant="outline" className="min-h-12" onClick={() => window.open(file.url, "_blank", "noopener")}>
        <ExternalLink className="size-4" />
        <span className="ml-1.5">Открыть</span>
      </Button>
    );
  }
  const text = file.state === "hidden" ? fileHiddenText(formatDocDateTime(file.hiddenAt)) : LABEL_TEXT.fileNone;
  return <span className="text-xs text-on-surface-variant">{text}</span>;
}

interface Props {
  job: LabelPrintJobDto;
  reprinting: boolean;
  /** Не передан — кнопки «Перепечатать» нет. */
  onReprint?: (job: LabelPrintJobDto) => void;
  /** Не передан — «⋯» со «Скрыть файл» нет. */
  onHideRequest?: (job: LabelPrintJobDto) => void;
}

export function LabelJobActions({ job, reprinting, onReprint, onHideRequest }: Props) {
  const when = formatDocDateTime(job.createdAt);
  const canHide = onHideRequest !== undefined && job.file.state === "available";
  return (
    <div className="flex flex-wrap items-center gap-2">
      <FileCell job={job} />
      {onReprint ? (
        <Button variant="outline" className="min-h-12" onClick={() => onReprint(job)} disabled={reprinting}>
          {reprinting ? <Loader2 className="size-4 animate-spin" /> : <RotateCcw className="size-4" />}
          <span className="ml-1.5">{LABEL_TEXT.reprint}</span>
          <span className="sr-only"> задачу от {when}</span>
        </Button>
      ) : null}
      {canHide ? (
        <RowActionsMenu
          title={`Печать от ${when}`}
          ariaLabel={`Действия с печатью от ${when}`}
          size="wms"
          actions={[{ key: "hide", label: LABEL_TEXT.hideFile, icon: EyeOff, run: () => onHideRequest(job) }]}
        />
      ) : null}
    </div>
  );
}
